/**
 * Salesforce EventSource Service
 * Handles Server-Sent Events (SSE) for real-time messaging
 */

import { SALESFORCE_CONSTANTS, SALESFORCE_EVENT_TYPES, CONNECTION_STATUS } from './constants';
import { getCurrentJwt } from './authService';
import '../helpers/eventsource-polyfill.js'; // Ensure polyfill is loaded
import { 
  getOrganizationId, 
  getMessagingUrl, 
  getLastEventId,
  storeLastEventId
} from './webStorageService';

// EventSource instance
let eventSource = null;
let reconnectAttempts = 0;
let reconnectTimeout = null;

/**
 * Get EventSource connection parameters
 * @param {boolean} forceFresh - If true, omit Last-Event-ID to get fresh event stream
 */
const getEventSourceParams = async (forceFresh = false) => {
  const jwt = getCurrentJwt();
  const organizationId = getOrganizationId();
  const lastEventId = forceFresh ? null : getLastEventId();

  if (!jwt) {
    throw new Error('No JWT token available for EventSource connection. Ensure authentication is completed before creating EventSource.');
  }

  if (!organizationId) {
    throw new Error('Missing organization ID for EventSource connection');
  }

  return {
    headers: {
      'Authorization': `Bearer ${jwt}`,
      'X-Org-ID': organizationId,
      ...(lastEventId && { 'Last-Event-ID': lastEventId })
    },
    heartbeatTimeout: 90000  // Match sample app exactly
  };
};

/**
 * Handle EventSource listeners
 */
const handleEventSourceListeners = (operation, eventListeners) => {
  if (!eventSource || typeof eventSource[operation] !== 'function') {
    throw new Error(`Invalid EventSource instance or operation: ${operation}`);
  }

  Object.entries(eventListeners).forEach(([eventName, eventHandler]) => {
    if (typeof eventName !== 'string' || typeof eventHandler !== 'function') {
      console.warn(`Invalid event listener: ${eventName}`);
      return;
    }

    eventSource[operation](eventName, eventHandler);
  });
};

/**
 * Create and configure EventSource connection
 * @param {Object} eventListeners - Event listener map
 * @param {boolean} forceFresh - If true, create fresh connection without Last-Event-ID
 */
export const createEventSource = (eventListeners = {}, forceFresh = false) => {
  return new Promise(async (resolve, reject) => {
    try {
      const messagingUrl = getMessagingUrl();
      if (!messagingUrl) {
        throw new Error('Missing messaging URL for EventSource connection');
      }

      const apiPath = `${messagingUrl}/eventrouter/v1/sse?_ts=${Date.now()}`;
      const params = await getEventSourceParams(forceFresh); // Pass forceFresh parameter
      
      // The polyfill is exported as an object with EventSourcePolyfill property
      const EventSourceConstructor = window.EventSourcePolyfill?.EventSourcePolyfill || window.EventSourcePolyfill;
      
      // Check for polyfill availability
      if (!EventSourceConstructor || typeof EventSourceConstructor !== 'function') {
        console.error('[Salesforce:EventSource] EventSourcePolyfill not available');
        throw new Error('EventSourcePolyfill is not a constructor.');
      }
      
      try {
        eventSource = new EventSourceConstructor(apiPath, params);
      } catch (constructorError) {
        console.error('[Salesforce:EventSource] Failed to create EventSource:', constructorError);
        throw new Error(`Failed to create EventSource: ${constructorError.message}`);
      }

      // Handle connection opened
      eventSource.onopen = () => {
        reconnectAttempts = 0; // Reset reconnect attempts on successful connection
        
        // Add event listeners
        if (Object.keys(eventListeners).length > 0) {
          handleEventSourceListeners('addEventListener', eventListeners);
        }
        
        resolve(eventSource);
      };

      // Handle connection errors
      eventSource.onerror = (error) => {
        console.error('[Salesforce:EventSource] Connection error:', error);
        
        if (eventSource.readyState === EventSource.CLOSED) {
          reject(new Error('EventSource connection failed and cannot reconnect'));
        }
      };

      // Handle messages with Last-Event-ID tracking
      eventSource.onmessage = (event) => {

        if (event.lastEventId) {
          storeLastEventId(event.lastEventId);
        }
        
        // Parse and route the message to appropriate handlers
        try {
          const eventData = JSON.parse(event.data);
          
          // Route based on conversationEntry.entryType if present
          if (eventData.conversationEntry && eventData.conversationEntry.entryType) {
            const entry = eventData.conversationEntry;
            const entryType = entry.entryType;
            
            // Map entryType to event handler and prepare appropriate data
            let handlerEventType = null;
            let handlerData = eventData;
            
            switch (entryType) {
              case 'ParticipantChanged':
                handlerEventType = 'CONVERSATION_PARTICIPANT_CHANGED';
                // For participant changes, use the entry payload
                try {
                  if (entry.entryPayload) {
                    const payloadData = typeof entry.entryPayload === 'string' 
                      ? JSON.parse(entry.entryPayload) 
                      : entry.entryPayload;
                    handlerData = { ...eventData, participantData: payloadData };
                  }
                } catch (parseError) {
                  console.error('[Salesforce:EventSource] Failed to parse ParticipantChanged:', parseError);
                }
                break;
                
              case 'Message':
                handlerEventType = 'CONVERSATION_MESSAGE';
                // For messages, extract the message content from entryPayload
                try {
                  if (entry.entryPayload) {
                    const messagePayload = typeof entry.entryPayload === 'string' 
                      ? JSON.parse(entry.entryPayload) 
                      : entry.entryPayload;
                    
                    // Create a properly formatted message object
                    handlerData = {
                      id: entry.identifier || `msg_${Date.now()}`,
                      text: messagePayload.text || messagePayload.messageText || '',
                      messageText: messagePayload.text || messagePayload.messageText || '',
                      timestamp: entry.transcriptedTimestamp || entry.clientTimestamp || Date.now(),
                      sender: eventData.conversationEntry?.sender?.role === 'Agent' ? 'agent' : 'user',
                      senderDisplayName: entry.senderDisplayName || 'Unknown',
                      type: 'salesforce',
                      conversationId: eventData.conversationId,
                      rawEventData: eventData
                    };
                  }
                } catch (parseError) {
                  console.error('[Salesforce:EventSource] Failed to parse Message:', parseError);
                  // Fallback to basic message structure
                  handlerData = {
                    id: entry.identifier || `msg_${Date.now()}`,
                    text: 'Message received',
                    messageText: 'Message received',
                    timestamp: Date.now(),
                    sender: 'agent',
                    type: 'salesforce',
                    conversationId: eventData.conversationId
                  };
                }
                break;
                
              case 'TypingStartedIndicator':
                handlerEventType = 'CONVERSATION_TYPING_STARTED_INDICATOR';
                try {
                  if (entry.entryPayload) {
                    const typingPayload = typeof entry.entryPayload === 'string' 
                      ? JSON.parse(entry.entryPayload) 
                      : entry.entryPayload;
                    handlerData = { ...eventData, participantId: typingPayload.participantId };
                  }
                } catch (parseError) {
                  console.error('[Salesforce:EventSource] Failed to parse TypingStartedIndicator:', parseError);
                }
                break;
                
              case 'TypingStoppedIndicator':
                handlerEventType = 'CONVERSATION_TYPING_STOPPED_INDICATOR';
                try {
                  if (entry.entryPayload) {
                    const typingPayload = typeof entry.entryPayload === 'string' 
                      ? JSON.parse(entry.entryPayload) 
                      : entry.entryPayload;
                    handlerData = { ...eventData, participantId: typingPayload.participantId };
                  }
                } catch (parseError) {
                  console.error('[Salesforce:EventSource] Failed to parse TypingStoppedIndicator:', parseError);
                }
                break;
                
              default:
                break;
            }
            
            // Trigger the appropriate event listener if we have one
            if (handlerEventType && eventListeners[handlerEventType]) {
              eventListeners[handlerEventType]({ data: handlerData });
            } else if (handlerEventType) {
              console.warn('[Salesforce:EventSource] No handler registered for ' + handlerEventType);
            }
          }
          
        } catch (error) {
          console.error('[Salesforce:EventSource] Failed to parse message:', error, event.data);
        }
      };

      // Handle ping events (heartbeat/keepalive)
      eventSource.addEventListener('ping', (event) => {
        console.debug('[Salesforce:EventSource] Heartbeat ping received');
      });

      // Handle server error events
      eventSource.addEventListener('error', (event) => {
        if (event.data) {
          try {
            const errorData = JSON.parse(event.data);
            console.warn('[Salesforce:EventSource] Server error:', errorData);
          } catch (parseError) {
            console.warn('[Salesforce:EventSource] Server error (unparseable):', event.data);
          }
        }
      });

    } catch (error) {
      console.error('[Salesforce:EventSource] Failed to create:', error);
      reject(error);
    }
  });
};

/**
 * Add event listeners to existing EventSource
 */
export const addEventListener = (eventName, eventHandler) => {
  if (!eventSource) {
    throw new Error('No active EventSource connection');
  }
  
  eventSource.addEventListener(eventName, eventHandler);
};

/**
 * Remove event listeners from EventSource
 */
export const removeEventListener = (eventName, eventHandler) => {
  if (!eventSource) {
    return; // Silently return if no connection
  }
  
  eventSource.removeEventListener(eventName, eventHandler);
};

/**
 * Subscribe to EventSource with automatic reconnection
 * @param {Object} eventListeners - Event listener map
 * @param {Function} onConnectionStatusChange - Connection status callback
 * @param {boolean} forceFresh - If true, create fresh connection without Last-Event-ID
 */
export const subscribeToEventSource = (eventListeners = {}, onConnectionStatusChange = null, forceFresh = false) => {
  const attemptConnection = () => {
    if (onConnectionStatusChange) {
      onConnectionStatusChange(CONNECTION_STATUS.CONNECTING);
    }

    createEventSource(eventListeners, forceFresh)
      .then(() => {

        if (onConnectionStatusChange) {
          onConnectionStatusChange(CONNECTION_STATUS.CONNECTED);
        }
      })
      .catch((error) => {
        console.error('[Salesforce:EventSource] Connection failed:', error);
        
        if (onConnectionStatusChange) {
          onConnectionStatusChange(CONNECTION_STATUS.ERROR);
        }

        // Attempt reconnection with exponential backoff
        if (reconnectAttempts < SALESFORCE_CONSTANTS.MAX_RECONNECT_ATTEMPTS) {
          reconnectAttempts++;
          const delay = Math.min(
            SALESFORCE_CONSTANTS.RECONNECT_BASE_DELAY * Math.pow(2, reconnectAttempts - 1),
            30000 // Max 30 seconds
          );
          
          reconnectTimeout = setTimeout(() => {
            attemptConnection();
          }, delay);
        } else {
          console.error('[Salesforce:EventSource] Max reconnection attempts reached');
          if (onConnectionStatusChange) {
            onConnectionStatusChange(CONNECTION_STATUS.DISCONNECTED);
          }
        }
      });
  };

  attemptConnection();
};

/**
 * Close EventSource connection
 */
export const closeEventSource = () => {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }

  if (eventSource) {
    eventSource.close();
    eventSource = null;
  }

  reconnectAttempts = 0;
};

/**
 * Get current connection status
 */
export const getConnectionStatus = () => {
  if (!eventSource) {
    return CONNECTION_STATUS.DISCONNECTED;
  }

  switch (eventSource.readyState) {
    case EventSource.CONNECTING:
      return CONNECTION_STATUS.CONNECTING;
    case EventSource.OPEN:
      return CONNECTION_STATUS.CONNECTED;
    case EventSource.CLOSED:
      return CONNECTION_STATUS.DISCONNECTED;
    default:
      return CONNECTION_STATUS.ERROR;
  }
};

/**
 * Check if EventSource is connected
 */
export const isConnected = () => {
  return eventSource && eventSource.readyState === EventSource.OPEN;
};

/**
 * Get current EventSource instance (for debugging)
 */
export const getCurrentEventSource = () => {
  return eventSource;
};
